/**
 * AP Simulation Engine — runtime that consumes the registry in `ap-simulations.ts`.
 *
 * Responsibilities:
 *   1. On AP entry, evaluate greeting predicates against runtime context and
 *      enqueue agent messages for ChatRail to render.
 *   2. Intercept user messages that match a registered intent (or the active
 *      follow-up from a just-fired greeting), respond with canned messages,
 *      and dispatch SimActions AFTER the agent finishes "speaking" — so the
 *      simulation reads as a real agent: message → pause → action.
 *   3. Resolve placeholders in messages + actions against runtime context
 *      (record ids and human-readable record names from the actual DB).
 *
 * The engine is intentionally pragmatic: it knows about a small set of
 * predicates and actions hand-coded against the demo flow. New entries in
 * the registry that don't have a matching runtime handler stay `status: 'todo'`.
 */

import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef } from "react"
import { api } from "../api/client"
import { useExportOperationsStore } from "../components/autopilots/shared/exporter-cockpit/exportOperationsStore"
import { useStore } from "../store/useStore"
import type { QmsRecord } from "../types"
import {
  AP_SIMULATIONS,
  getApSimulation,
  type APSimulation,
  type SimAction,
  type SimGreeting,
  type SimIntent,
  type SimMessage,
} from "./ap-simulations"

// ── Engine context ────────────────────────────────────────────────────────────

/** Snapshot the engine resolves placeholders + predicates against. */
interface EngineContext {
  apId: string
  /** First record_id flagged in this AP's Ausstehend partition, if any. */
  firstAusstehendRecordId: string | null
  firstAusstehendName: string | null
  /** All resolvable display names in the Ausstehend partition (in order). */
  ausstehendNames: string[]
  /** First pending document id awaiting analysis, if any. */
  firstPendingDocTargetId: string | null
  firstPendingDocTargetName: string | null
  /** First fresh-customer record id (customer-conformity). */
  firstFreshCustomerId: string | null
  firstFreshCustomerName: string | null
  /** Currently-stale (or recently-demoted) record ids — surfaces "demoted" notifications. */
  hasDemotedCustomers: boolean
}

/** Join list: ["A"] → "A", ["A","B"] → "A and B", ["A","B","C"] → "A, B and C". */
function joinList(items: string[], isEnglish: boolean): string {
  if (items.length === 0) return ""
  if (items.length === 1) return items[0]
  const conj = isEnglish ? " and " : " und "
  return `${items.slice(0, -1).join(", ")}${conj}${items[items.length - 1]}`
}

// ── Pattern matcher ───────────────────────────────────────────────────────────

function normalize(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[?!.,]+$/, "")
}

function matchesIntent(intent: SimIntent, text: string): boolean {
  const norm = normalize(text)
  if ("exact" in intent.pattern) {
    return intent.pattern.exact.some((p) => normalize(p) === norm)
  }
  return intent.pattern.regex.test(text)
}

// ── Name resolution ───────────────────────────────────────────────────────────

function recordDisplayName(rec: QmsRecord, scopeId: string): string {
  return (
    (rec.values[`${scopeId}_name`]?.value as string | undefined) ??
    (rec.values["name"]?.value as string | undefined) ??
    (rec.values[`${scopeId}_id`]?.value as string | undefined) ??
    rec.id
  )
}

function lookupName(
  records: QmsRecord[] | undefined,
  id: string | null,
  scopeId: string,
): string | null {
  if (!records || !id) return null
  const r = records.find((rec) => rec.id === id)
  return r ? recordDisplayName(r, scopeId) : null
}

// ── Placeholder resolution ────────────────────────────────────────────────────

function resolveAction(action: SimAction, ctx: EngineContext): SimAction {
  // Walk every string field; replace [record.id] with the best candidate from ctx.
  const resolved: Record<string, unknown> = {
    ...(action as unknown as Record<string, unknown>),
  }
  const recordId =
    ctx.firstAusstehendRecordId ??
    ctx.firstPendingDocTargetId ??
    ctx.firstFreshCustomerId ??
    null
  for (const [k, v] of Object.entries(resolved)) {
    if (typeof v !== "string") continue
    if (v === "[record.id]" && recordId) resolved[k] = recordId
  }
  return resolved as unknown as SimAction
}

function resolveTemplate(
  text: string,
  ctx: EngineContext,
  isEnglish: boolean,
  translate: (k: TranslationKeys) => string,
): string {
  // Translate simulated message keys dynamically if they refer to translation keys.
  const resolvedText = text.startsWith("QMS/CHAT_SIM/")
    ? translate(text as TranslationKeys) || text
    : text

  // Pick the most relevant resolved name for this AP context.
  const name =
    ctx.firstAusstehendName ??
    ctx.firstPendingDocTargetName ??
    ctx.firstFreshCustomerName ??
    null
  let out = resolvedText
  if (name) {
    out = out
      .replaceAll("[lieferant]", name)
      .replaceAll("[Lieferant]", name)
      .replaceAll("[lieferant.name]", name)
      .replaceAll("[kunde]", name)
      .replaceAll("[kunde.name]", name)
      .replaceAll("[record.name]", name)
  }
  if (ctx.ausstehendNames.length > 0) {
    out = out.replaceAll(
      "[lieferanten.liste]",
      joinList(ctx.ausstehendNames, isEnglish),
    )
  }
  out = out.replaceAll("[record.id]", ctx.firstAusstehendRecordId ?? "")
  return out
}

// ── Predicate evaluation ──────────────────────────────────────────────────────

function evaluatePredicate(greeting: SimGreeting, ctx: EngineContext): boolean {
  switch (greeting.predicate.kind) {
    case "always":
      return true
    case "has_open_ausstehend":
      // Wait for the name to be resolved before firing — otherwise the greeting
      // would render with a literal "[lieferant]" placeholder.
      return (
        ctx.firstAusstehendRecordId !== null && ctx.firstAusstehendName !== null
      )
    case "has_pending_document":
      return (
        ctx.firstPendingDocTargetId !== null &&
        ctx.firstPendingDocTargetName !== null
      )
    case "has_fresh_customer_update":
      return (
        (ctx.firstFreshCustomerId !== null &&
          ctx.firstFreshCustomerName !== null) ||
        ctx.hasDemotedCustomers
      )
    case "has_open_finding":
      return false // not used in the recording flow yet
    default:
      return false
  }
}

// ── Action dispatcher ─────────────────────────────────────────────────────────

interface DispatchDeps {
  apId: string
  queryClient: ReturnType<typeof useQueryClient>
  translate: (k: TranslationKeys) => string
}

async function dispatchAction(
  action: SimAction,
  deps: DispatchDeps,
): Promise<void> {
  const store = useStore.getState()
  const opsStore = useExportOperationsStore.getState()
  switch (action.type) {
    case "open_record": {
      const sim = getApSimulation(deps.apId)
      // Importer APs land the record on activeBucketRecordId; everything else
      // (exporter, monitor with detail) uses activeExportRecordId.
      if (sim?.label.toLowerCase().includes("lieferanten deklaration")) {
        store.setActiveBucket(action.record_id)
      } else {
        store.setActiveExport(action.record_id)
      }
      break
    }
    case "create_email_draft": {
      const cockpitTemplates = (() => {
        if (deps.apId === "lieferanten-abfragen") return ["fragebogen-v3"]
        if (deps.apId === "customer-conformity") return ["kunden-konformitaet"]
        return ["default"]
      })()
      // Make sure the cockpit is open on the right record before creating
      // the draft so the timeline immediately reveals it.
      store.setActiveExport(action.record_id)
      opsStore.addDraft({
        ap_id: deps.apId,
        record_id: action.record_id,
        template_ids: action.template_ids ?? cockpitTemplates,
        email_subject: deps.translate("QMS/CHAT_SIM/DEFAULT_EMAIL_SUBJECT"),
      })
      break
    }
    case "start_bucket_import": {
      // SupplierAktionenTab watches this flag and runs handleImport.
      store.setPendingAutoImportRecordId(action.record_id)
      break
    }
    case "simulate_reply_after": {
      window.setTimeout(() => {
        opsStore.simulateReply(deps.apId, action.op_id).catch(() => {
          /* best-effort */
        })
      }, action.delay_ms)
      break
    }
    case "navigate_to_ap":
    case "open_session":
    case "accept_all":
    case "cancel_analysis":
      // Configuration-AP actions and AP navigation are not driven by the
      // simulation engine in this iteration. See registry notes.
      break
  }
}

// ── Special intent handlers ───────────────────────────────────────────────────
// Some intents need to call the backend before responding (e.g. running the
// monitor evaluator). They live here so the registry stays declarative.

async function handleSpecialIntent(
  apId: string,
  intent: SimIntent,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  if (apId === "lieferanten-tracking" && "regex" in intent.pattern) {
    try {
      await api.monitorEvaluate("lieferanten-tracking")
      queryClient.invalidateQueries({
        queryKey: ["monitor-findings-open", "lieferanten-tracking"],
      })
      queryClient.invalidateQueries({ queryKey: ["ap-notifications-summary"] })
    } catch {
      /* best-effort — narrative still plays */
    }
  }
}

// ── Timing helpers ────────────────────────────────────────────────────────────

/** Default per-message delay if the registry doesn't specify one. */
function defaultDelay(idx: number): number {
  return idx === 0 ? 800 : 1200
}

/** Time the ChatRail broadcast effect needs to render every message + the
 *  small buffer we want before dispatching actions, so the user reads the
 *  message before something starts moving on screen. */
function totalBroadcastMs(messages: SimMessage[]): number {
  let total = 0
  messages.forEach((m, i) => {
    total += m.delayMs ?? defaultDelay(i)
  })
  return total + 600 // post-message buffer
}

// ── Engine hook ───────────────────────────────────────────────────────────────

export interface SimEngineApi {
  /**
   * Try to handle a user message via the registry. Returns true if the
   * engine intercepted (caller should NOT post to the backend chat route).
   */
  tryIntercept: (text: string) => boolean
}

/**
 * Hook used by ChatRail. Pulls live runtime context from React Query and the
 * stores, fires greetings on mount/predicate transitions, and offers
 * `tryIntercept` for the user-message path.
 */
export function useApSimulationEngine(apId: string): SimEngineApi {
  const { translate, currentLanguage } = useTranslation()
  const isEnglish = currentLanguage === "EN"
  const queryClient = useQueryClient()
  const setBroadcast = useStore((s) => s.setPendingAgentBroadcast)
  const dbConfigured = useStore((s) => s.dbConfigured)
  const exporterThreshold = useStore((s) => s.exporterThreshold)

  // Per-mount refs.
  const activeFollowUpRef = useRef<SimIntent | null>(null)
  const pendingActionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const firedGreetingKeyRef = useRef<string | null>(null)

  // Cancel any in-flight delayed action when the AP unmounts (user navigates
  // away mid-delay) so we don't dispatch into a different AP context.
  useEffect(() => {
    return () => {
      if (pendingActionTimerRef.current) {
        clearTimeout(pendingActionTimerRef.current)
        pendingActionTimerRef.current = null
      }
    }
  }, [])

  const sim: APSimulation | undefined = AP_SIMULATIONS[apId]

  // ── Live data sources ───────────────────────────────────────────────────────
  // Each predicate-relevant query is gated on `apId`-relevance so we don't
  // over-fetch in unrelated APs. React Query's cache is shared, so polls
  // already running in landing/cockpit code are reused.

  const { data: pendingDocs } = useQuery({
    queryKey: ["pendingDocuments", apId],
    queryFn: () => api.pendingDocumentsList(apId),
    enabled: dbConfigured === true && apId === "supplier",
    staleTime: 5_000,
  })

  const { data: freshness } = useQuery({
    queryKey: ["freshness", exporterThreshold],
    queryFn: () => api.freshnessPartition(exporterThreshold),
    enabled: dbConfigured === true && apId === "customer-conformity",
    staleTime: 15_000,
  })

  const { data: apPartition } = useQuery({
    queryKey: ["ap-partition", apId, exporterThreshold],
    queryFn: () => api.apPartition(apId, exporterThreshold),
    enabled: dbConfigured === true && apId === "lieferanten-abfragen",
    staleTime: 15_000,
  })

  // Records lookups — used to resolve display names for placeholder
  // substitution. One query per scope; cache is shared with the rest of the app.
  const supplierRelevant =
    apId === "supplier" || apId === "lieferanten-abfragen"
  const { data: supplierRecordsPage } = useQuery({
    queryKey: ["records", "supplier"],
    queryFn: () => api.listRecords("supplier"),
    enabled: dbConfigured === true && supplierRelevant,
    staleTime: 30_000,
  })
  const customerRelevant = apId === "customer-conformity"
  const { data: customerRecordsPage } = useQuery({
    queryKey: ["records", "customer"],
    queryFn: () => api.listRecords("customer"),
    enabled: dbConfigured === true && customerRelevant,
    staleTime: 30_000,
  })

  const supplierRecords = supplierRecordsPage?.records
  const customerRecords = customerRecordsPage?.records

  // ── Build context ───────────────────────────────────────────────────────────

  const firstAusstehendRecordId =
    apPartition?.ausstehend?.[0]?.record_id ?? null
  const firstPendingDocTargetId =
    pendingDocs?.find((d) => !!d.target_record_id)?.target_record_id ?? null
  const firstFreshCustomerId = freshness?.fresh_record_ids?.[0] ?? null

  const ausstehendNames = (apPartition?.ausstehend ?? [])
    .map((entry) => lookupName(supplierRecords, entry.record_id, "supplier"))
    .filter((n): n is string => !!n)

  const ctx: EngineContext = {
    apId,
    firstAusstehendRecordId,
    firstAusstehendName: lookupName(
      supplierRecords,
      firstAusstehendRecordId,
      "supplier",
    ),
    ausstehendNames,
    firstPendingDocTargetId,
    firstPendingDocTargetName: lookupName(
      supplierRecords,
      firstPendingDocTargetId,
      "supplier",
    ),
    firstFreshCustomerId,
    firstFreshCustomerName: lookupName(
      customerRecords,
      firstFreshCustomerId,
      "customer",
    ),
    hasDemotedCustomers: Object.values(
      freshness?.stalest_relation_per_record ?? {},
    ).some((entry) => entry?.demotion_reason),
  }

  // ── Greeting effect ─────────────────────────────────────────────────────────
  // Fires when the predicate transitions to true AND the data needed to
  // render the message (record name) has resolved. We include names in the
  // dep key so a slow records query still triggers a re-eval once the name
  // shows up.

  const greetingsKey = JSON.stringify({
    apId,
    a: ctx.firstAusstehendRecordId,
    aName: ctx.firstAusstehendName,
    aNames: ctx.ausstehendNames,
    p: ctx.firstPendingDocTargetId,
    pName: ctx.firstPendingDocTargetName,
    f: ctx.firstFreshCustomerId,
    fName: ctx.firstFreshCustomerName,
    d: ctx.hasDemotedCustomers,
  })

  useEffect(() => {
    if (!sim) return
    for (const greeting of sim.greetings) {
      if (greeting.status === "todo") continue
      if (!evaluatePredicate(greeting, ctx)) continue
      const key = `${apId}:${JSON.stringify(greeting.predicate)}`
      if (firedGreetingKeyRef.current === key) continue
      firedGreetingKeyRef.current = key
      setBroadcast({
        apId,
        messages: greeting.messages.map((m, i) => ({
          text: resolveTemplate(m.text, ctx, isEnglish, translate),
          delayMs: m.delayMs ?? (i === 0 ? 1500 : 1200),
        })),
      })
      activeFollowUpRef.current = greeting.followUp ?? null
      break // only one greeting per mount, in registry order
    }
    // greetingsKey covers every ctx field a predicate or template reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greetingsKey])

  // ── User-message intercept ──────────────────────────────────────────────────

  function scheduleIntent(intent: SimIntent, snapshot: EngineContext): void {
    // Cancel any earlier pending action so the latest user input wins.
    if (pendingActionTimerRef.current) {
      clearTimeout(pendingActionTimerRef.current)
      pendingActionTimerRef.current = null
    }
    setBroadcast({
      apId: snapshot.apId,
      messages: intent.messages.map((m, i) => ({
        text: resolveTemplate(m.text, snapshot, isEnglish, translate),
        delayMs: m.delayMs ?? defaultDelay(i),
      })),
    })
    const delayMs = totalBroadcastMs(intent.messages)
    pendingActionTimerRef.current = setTimeout(() => {
      pendingActionTimerRef.current = null
      runIntent(intent, snapshot, queryClient, translate).catch(() => {
        /* best-effort */
      })
    }, delayMs)
  }

  function tryIntercept(text: string): boolean {
    if (!sim) return false
    // Configuration AP intents (alles richtig / verwerfen / hilfe) are owned
    // by the backend SSE handler — intercepting here would short-circuit the
    // diff acceptance and Anwenden would never enable.
    if (apId === "configuration") return false

    // Active follow-up from the most-recent greeting takes precedence.
    const followUp = activeFollowUpRef.current
    if (
      followUp &&
      followUp.status !== "todo" &&
      matchesIntent(followUp, text)
    ) {
      activeFollowUpRef.current = null
      scheduleIntent(followUp, ctx)
      return true
    }

    // Then registered AP intents.
    for (const intent of sim.intents) {
      if (intent.status === "todo") continue
      if (!matchesIntent(intent, text)) continue
      scheduleIntent(intent, ctx)
      return true
    }
    return false
  }

  return { tryIntercept }
}

// ── Intent execution ──────────────────────────────────────────────────────────

async function runIntent(
  intent: SimIntent,
  ctx: EngineContext,
  queryClient: ReturnType<typeof useQueryClient>,
  translate: (k: TranslationKeys) => string,
): Promise<void> {
  await handleSpecialIntent(ctx.apId, intent, queryClient)
  for (const action of intent.actions) {
    const resolved = resolveAction(action, ctx)
    await dispatchAction(resolved, { apId: ctx.apId, queryClient, translate })
  }
}
